
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Main } from '@/components/layout/main';
import { PageHeader } from '@/components/layout/page-header';
import { LayoutDashboard } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';


import { superAdminService } from '@/api/super-admin';
import { SuperAdminSystemStats } from '@/types/super-admin';
import { handleServerError } from '@/utils/handle-server-error';
import {
  Building2,
  Users,
  UserCheck,
  Shield,
  ShieldCheck,
  FileText,
  Calendar,
  Activity,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Inbox
} from 'lucide-react';

interface ActionCardProps {
  title: string;
  count: number;
  recent: number;
  recentLabel: string;
  emptyLabel: string;
  actionLabel: string;
  to: string;
  icon: React.ReactNode;
}

/**
 * A queue that needs a human decision. Reads as "N waiting" when there is work
 * and collapses to a calm confirmation when the queue is empty, so the
 * dashboard never nags about something that is already handled.
 */
function ActionCard({ title, count, recent, recentLabel, emptyLabel, actionLabel, to, icon }: ActionCardProps) {
  const hasWork = count > 0;
  return (
    <Card className={hasWork ? 'border-amber-300 bg-amber-50/40' : undefined}>
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <span
            className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
              hasWork ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'
            }`}
          >
            {hasWork ? icon : <CheckCircle2 className="h-5 w-5 text-green-600" />}
          </span>
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {hasWork ? (
              <p className="text-3xl font-semibold leading-none">{count}</p>
            ) : (
              <p className="text-base font-medium">{emptyLabel}</p>
            )}
            <Badge variant="outline" className="mt-1 font-normal">
              {recent} {recentLabel}
            </Badge>
          </div>
        </div>

        <Button asChild variant={hasWork ? 'default' : 'outline'} size="sm" className="shrink-0">
          <Link to={to}>
            {actionLabel}
            <ArrowRight className="ms-2 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
}

function StatCard({ title, value, icon, description, trend }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-4 w-4 text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">
            {trend && (
              <span className={`inline - flex items - center ${trend === 'up' ? 'text-green-600' :
                trend === 'down' ? 'text-red-600' :
                  'text-gray-600'
                } `}>
                {trend === 'up' && <TrendingUp className="h-3 w-3 mr-1" />}
                {trend === 'down' && <TrendingUp className="h-3 w-3 mr-1 rotate-180" />}
                {trend === 'neutral' && <Activity className="h-3 w-3 mr-1" />}
              </span>
            )}
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function SuperAdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<SuperAdminSystemStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await superAdminService.getDashboardStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching super admin stats:', error);
        handleServerError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 min-w-0">

          <Main>
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">{t('loading')}</p>
              </div>
            </div>
          </Main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">

        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <LayoutDashboard className="h-5 w-5" />
                </span>
              }
              title={t('super_admin_dashboard')}
              description={t('super_admin_dashboard_description')}
            />

            {stats && (
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('sa_to_process') || 'À traiter'}
                  </h2>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <ActionCard
                    title={t('sa_pending_cabinets') || 'Inscriptions en attente'}
                    count={stats.pending_cabinets}
                    recent={stats.cabinets_last_7d}
                    recentLabel={t('sa_last_7d') || 'sur 7 jours'}
                    emptyLabel={t('sa_nothing_pending') || 'Rien en attente'}
                    actionLabel={t('sa_review_cabinets') || 'Traiter les demandes'}
                    to="/super-admin-cabinets"
                    icon={<Clock3 className="h-5 w-5" />}
                  />
                  <ActionCard
                    title={t('sa_pending_listing_requests') || 'Demandes de référencement'}
                    count={stats.pending_listing_requests}
                    recent={stats.listing_requests_last_7d}
                    recentLabel={t('sa_last_7d') || 'sur 7 jours'}
                    emptyLabel={t('sa_nothing_pending') || 'Rien en attente'}
                    actionLabel={t('sa_review_listing_requests') || 'Traiter les demandes'}
                    to="/super-admin-listing-requests"
                    icon={<Inbox className="h-5 w-5" />}
                  />
                </div>
              </section>
            )}

            {stats && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {/* Cabinet Stats */}
                <StatCard
                  title={t('total_cabinets')}
                  value={stats.total_cabinets}
                  icon={<Building2 />}
                  description={t('total_registered_cabinets')}
                />
                <StatCard
                  title={t('active_cabinets')}
                  value={stats.active_cabinets}
                  icon={<Building2 className="text-green-600" />}
                  description={t('currently_active_cabinets')}
                  trend="up"
                />
                <StatCard
                  title={t('disabled_cabinets')}
                  value={stats.disabled_cabinets}
                  icon={<Building2 className="text-red-600" />}
                  description={t('temporarily_disabled_cabinets')}
                  trend="down"
                />

                {/* User Stats */}
                <StatCard
                  title={t('total_users')}
                  value={stats.total_users}
                  icon={<Users />}
                  description={t('all_system_users')}
                />
                <StatCard
                  title={t('active_users')}
                  value={stats.active_users}
                  icon={<UserCheck className="text-green-600" />}
                  description={t('currently_active_users')}
                  trend="up"
                />
                <StatCard
                  title={t('cabinet_admins')}
                  value={stats.cabinet_admins}
                  icon={<Shield className="text-blue-600" />}
                  description={t('cabinet_administrators')}
                />
                <StatCard
                  title={t('super_admins')}
                  value={stats.super_admins}
                  icon={<ShieldCheck className="text-purple-600" />}
                  description={t('system_super_administrators')}
                />

                {/* Case Stats */}
                <StatCard
                  title={t('total_cases')}
                  value={stats.total_cases}
                  icon={<FileText />}
                  description={t('all_cases_in_system')}
                />
                <StatCard
                  title={t('cases_this_month')}
                  value={stats.cases_this_month}
                  icon={<Calendar className="text-blue-600" />}
                  description={t('new_cases_current_month')}
                  trend="up"
                />
              </div>
            )}

          </div>
        </Main>
      </div>
    </div>
  );
}